import { Box, Text } from '@chakra-ui/react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, Tooltip, XAxis, YAxis } from 'recharts';

import type { MillageData } from '~/components/visualization/MillageLineChart';
import { StackedRevenueChart } from '~/components/visualization/StackedRevenueChart';
import type { Candidate } from '~/types/candidate';
import { formatCurrency } from '~/utils/formatting-utils';
import {
  aggregateCashflowsByYear,
  formatForStackedBarChart,
} from '~/utils/visualization-calculators/cashflow-aggregator';
import { formatExistingDebtMillageForChart } from '~/utils/visualization-calculators/existing-debt-millage';

// Color palette for bond series visualization
const BOND_COLORS = [
  '#3182ce', // blue
  '#38a169', // green
  '#d69e2e', // yellow
  '#e53e3e', // red
  '#805ad5', // purple
  '#dd6b20', // orange
];

interface CandidateGraphViewProps {
  candidate: Candidate | null;
}

export const CandidateGraphView: React.FC<CandidateGraphViewProps> = ({ candidate }) => {
  const [selectedTab, setSelectedTab] = useState('millage');
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(
    null
  );

  // Reset container size when candidate changes, then measure
  const candidateId = candidate?.id;
  useEffect(() => {
    // Reset size to trigger re-measurement when candidate changes
    // Using candidateId to track changes
    if (candidateId !== undefined) {
      setContainerSize(null);
    }
  }, [candidateId]);

  // Measure container dimensions
  useEffect(() => {
    if (containerSize) return; // Already have size

    const measureContainer = () => {
      if (chartContainerRef.current) {
        const { width, height } = chartContainerRef.current.getBoundingClientRect();
        if (width > 0 && height > 0) {
          // Account for padding (p={4} = 16px on each side)
          setContainerSize({ width: width - 32, height: height - 32 });
        }
      }
    };

    // Check after a brief delay for layout to settle
    const timeoutId = setTimeout(measureContainer, 10);

    return () => clearTimeout(timeoutId);
  }, [containerSize]);

  // Transform millage data for chart
  const millageData = useMemo<MillageData[]>(() => {
    if (!candidate?.millageInformation?.millageShape?.adjustments) {
      return [];
    }

    // Get millage map which has year -> rate mapping
    const millageMap = candidate.millageInformation.millageMap;
    if (!millageMap) {
      return [];
    }

    // Convert millage map to array format
    return Object.entries(millageMap)
      .map(([yearStr, rate]) => ({
        year: parseInt(yearStr, 10),
        rate,
      }))
      .sort((a, b) => a.year - b.year);
  }, [candidate]);

  // Calculate existing debt millage from revenueMap
  const existingDebtMillageData = useMemo(() => {
    if (!candidate?.revenueMap) {
      return [];
    }

    const millageByYear: Record<number, number> = {};
    Object.entries(candidate.revenueMap).forEach(([yearStr, entry]) => {
      const year = parseInt(yearStr, 10);
      const { existingDebt, taxableValue } = entry;

      if (taxableValue > 0 && existingDebt > 0) {
        // Millage = (Existing Debt / Taxable Value) * 1000
        const millage = (existingDebt / taxableValue) * 1000;
        millageByYear[year] = millage;
      }
    });

    return formatExistingDebtMillageForChart(millageByYear);
  }, [candidate]);

  // Transform revenue data for stacked bar chart
  const revenueChartData = useMemo(() => {
    if (!candidate) {
      return { data: [], series: [] };
    }

    const yearlyData = aggregateCashflowsByYear(candidate);
    return formatForStackedBarChart(yearlyData);
  }, [candidate]);

  // Get bond series info for legend
  const bondSeriesInfo = useMemo(() => {
    if (!candidate) return [];
    const yearlyPaymentData = aggregateCashflowsByYear(candidate);
    const bondSeriesMap = new Map<string, { id: string; name: string }>();

    yearlyPaymentData.forEach((yearData) => {
      Object.entries(yearData.bondSeries).forEach(([bondId, bondData]) => {
        if (!bondSeriesMap.has(bondId)) {
          bondSeriesMap.set(bondId, { id: bondId, name: bondData.name });
        }
      });
    });

    return Array.from(bondSeriesMap.values());
  }, [candidate]);

  // Transform data for overlay chart (existing debt + new bond payments stacked + revenue line on same scale)
  const overlayChartData = useMemo(() => {
    if (!candidate) {
      return [];
    }

    // Get revenue data by year from revenueMap (one entry per tax year, not duplicated)
    const revenueByYear = new Map<number, number>();
    if (candidate.revenueMap) {
      Object.entries(candidate.revenueMap).forEach(([yearStr, entry]) => {
        const year = parseInt(yearStr, 10);
        revenueByYear.set(year, entry.revenue);
      });
    }

    // Aggregate bond payments by year and bond series (includes existing debt)
    const yearlyPaymentData = aggregateCashflowsByYear(candidate);

    // Get all unique bond series IDs for consistent coloring
    const bondSeriesIds = new Set<string>();
    yearlyPaymentData.forEach((yearData) => {
      Object.keys(yearData.bondSeries).forEach((bondId) => {
        bondSeriesIds.add(bondId);
      });
    });
    const bondSeriesArray = Array.from(bondSeriesIds);

    // Combine into single dataset with existing debt + bond payments as separate properties
    const allYears = new Set([...revenueByYear.keys(), ...yearlyPaymentData.map((d) => d.year)]);

    return Array.from(allYears)
      .sort((a, b) => a - b)
      .map((year) => {
        const yearData = yearlyPaymentData.find((d) => d.year === year);
        const revenue = revenueByYear.get(year) || 0;

        // Build data object with existing debt + bond payments as separate properties
        const dataPoint: Record<string, number> = {
          year,
          revenue,
          existingDebt: yearData?.existingDebt || 0,
          totalPayments: yearData?.total || 0,
        };

        // Add each bond series as a separate property for stacked bars
        bondSeriesArray.forEach((bondId) => {
          const bondData = yearData?.bondSeries[bondId];
          dataPoint[bondId] = bondData?.payment || 0;
        });

        return dataPoint;
      });
  }, [candidate]);

  if (!candidate) {
    return (
      <Box
        height="500px"
        bg="gray.50"
        borderRadius="md"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        border="1px solid"
        borderColor="gray.200"
      >
        <Text fontSize="sm" color="gray.500">
          Select a candidate to view details
        </Text>
      </Box>
    );
  }

  return (
    <Box
      height="500px"
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      boxShadow="sm"
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      {/* Simple button-based tab navigation */}
      <Box borderBottom="1px solid" borderColor="gray.200" display="flex" gap={0}>
        <Box
          as="button"
          px={4}
          py={2}
          bg={selectedTab === 'millage' ? 'blue.50' : 'transparent'}
          borderBottom={selectedTab === 'millage' ? '2px solid' : 'none'}
          borderColor="blue.500"
          color={selectedTab === 'millage' ? 'blue.700' : 'gray.600'}
          fontWeight={selectedTab === 'millage' ? 'semibold' : 'normal'}
          onClick={() => setSelectedTab('millage')}
          cursor="pointer"
          _hover={{ bg: selectedTab === 'millage' ? 'blue.50' : 'gray.50' }}
        >
          Millage Shape
        </Box>
        <Box
          as="button"
          px={4}
          py={2}
          bg={selectedTab === 'revenue' ? 'blue.50' : 'transparent'}
          borderBottom={selectedTab === 'revenue' ? '2px solid' : 'none'}
          borderColor="blue.500"
          color={selectedTab === 'revenue' ? 'blue.700' : 'gray.600'}
          fontWeight={selectedTab === 'revenue' ? 'semibold' : 'normal'}
          onClick={() => setSelectedTab('revenue')}
          cursor="pointer"
          _hover={{ bg: selectedTab === 'revenue' ? 'blue.50' : 'gray.50' }}
        >
          Payments
        </Box>
        <Box
          as="button"
          px={4}
          py={2}
          bg={selectedTab === 'overlay' ? 'blue.50' : 'transparent'}
          borderBottom={selectedTab === 'overlay' ? '2px solid' : 'none'}
          borderColor="blue.500"
          color={selectedTab === 'overlay' ? 'blue.700' : 'gray.600'}
          fontWeight={selectedTab === 'overlay' ? 'semibold' : 'normal'}
          onClick={() => setSelectedTab('overlay')}
          cursor="pointer"
          _hover={{ bg: selectedTab === 'overlay' ? 'blue.50' : 'gray.50' }}
        >
          Overlay
        </Box>
      </Box>

      {/* Only render the active tab's content */}
      <Box ref={chartContainerRef} flex={1} overflow="hidden" minHeight={0} p={4}>
        {selectedTab === 'millage' &&
          (millageData.length > 0 && containerSize ? (
            <Box height="100%" position="relative">
              <ComposedChart
                width={containerSize.width}
                height={containerSize.height}
                data={millageData.map((item) => ({
                  ...item,
                  existingDebtMillage:
                    existingDebtMillageData.find((ed) => ed.year === item.year)?.rate || 0,
                }))}
                margin={{ top: 10, right: 30, left: 30, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" opacity={0.5} />
                <XAxis
                  dataKey="year"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => value.toString()}
                  label={{
                    value: 'Year',
                    angle: 0,
                    position: 'bottom',
                    offsetX: -10,
                    fontSize: 12,
                  }}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `${value.toFixed(2)}`}
                  label={{
                    value: 'Millage Rate',
                    angle: -90,
                    position: 'insideLeft',
                    offset: -10,
                    fontSize: 12,
                  }}
                />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name === 'existingDebtMillage') {
                      return [`${value.toFixed(3)} mills`, 'Existing Debt Baseline'];
                    }
                    return [`${value.toFixed(3)} mills`, 'Candidate Millage'];
                  }}
                  labelFormatter={(label) => `Year ${label}`}
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '6px',
                    padding: '8px',
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: '14px', paddingTop: '8px' }}
                  verticalAlign="top"
                  height={44}
                />
                {/* Existing debt baseline - DASHED GRAY LINE */}
                {existingDebtMillageData.length > 0 && (
                  <Line
                    type="monotone"
                    dataKey="existingDebtMillage"
                    stroke="#999"
                    strokeDasharray="5 5"
                    name="Existing Debt Baseline"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                )}
                {/* Candidate millage - SOLID BLUE LINE */}
                <Line
                  type="monotone"
                  dataKey="rate"
                  stroke="#3182ce"
                  strokeWidth={2}
                  name="Candidate Millage"
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </Box>
          ) : (
            <Box
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="gray.500"
            >
              <Text>No millage data available</Text>
            </Box>
          ))}

        {selectedTab === 'revenue' &&
          (revenueChartData.data.length > 0 && containerSize ? (
            <Box height="100%" position="relative">
              <StackedRevenueChart
                data={revenueChartData.data}
                series={revenueChartData.series}
                width={containerSize.width}
                height={containerSize.height}
              />
            </Box>
          ) : (
            <Box
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="gray.500"
            >
              <Text>No revenue data available</Text>
            </Box>
          ))}

        {selectedTab === 'overlay' &&
          (overlayChartData.length > 0 && containerSize ? (
            <Box height="100%" position="relative">
              <ComposedChart
                width={containerSize.width}
                height={containerSize.height}
                data={overlayChartData}
                margin={{ top: 10, right: 30, left: 30, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" opacity={0.5} />
                <XAxis
                  dataKey="year"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => value.toString()}
                  label={{
                    value: 'Year',
                    angle: 0,
                    position: 'bottom',
                    offsetX: -10,
                    fontSize: 12,
                  }}
                />
                {/* Single Y-axis for both payments and revenue (same scale) */}
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                  label={{
                    value: 'Amount',
                    angle: -90,
                    position: 'insideLeft',
                    offset: -10,
                    fontSize: 12,
                  }}
                />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name === 'revenue') {
                      return [
                        `${formatCurrency(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        'Revenue',
                      ];
                    }
                    if (name === 'existingDebt') {
                      return [
                        `${formatCurrency(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        'Existing Debt',
                      ];
                    }
                    // Find bond name for this bond ID
                    const bondInfo = bondSeriesInfo.find((b) => b.id === name);
                    const bondName = bondInfo?.name || name;
                    return [
                      `${formatCurrency(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                      bondName,
                    ];
                  }}
                  labelFormatter={(label) => `Year ${label}`}
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '6px',
                    padding: '8px',
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: '14px', paddingTop: '8px' }}
                  verticalAlign="top"
                  height={44}
                />
                {/* Existing debt bar (gray, bottom of stack) */}
                <Bar
                  dataKey="existingDebt"
                  stackId="payments"
                  fill="#718096"
                  name="Existing Debt"
                />
                {/* Stacked bars for each bond series */}
                {bondSeriesInfo.map((bond, idx) => (
                  <Bar
                    key={bond.id}
                    dataKey={bond.id}
                    stackId="payments"
                    fill={BOND_COLORS[idx % BOND_COLORS.length]}
                    name={bond.name}
                  />
                ))}
                {/* Revenue line overlay (should align closely with bar tops) */}
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#2c5282"
                  strokeWidth={3}
                  name="Revenue"
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </Box>
          ) : (
            <Box
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="gray.500"
            >
              <Text>No overlay data available</Text>
            </Box>
          ))}
      </Box>
    </Box>
  );
};
